// Phase 7 D3 — Track C T-C3. Server-backed ThemeProvider.
//
// Replaces Phase 6's in-memory implementation. Behaviour:
//
//   1. On mount we read the cookie / OS preference for an immediate paint
//      that matches whatever `theme-init.js` already set on <html>. The
//      synchronous initial state therefore avoids a render-time flicker
//      between Phase 6's behaviour and the server-resolved value.
//   2. After AuthProvider resolves and the user is authenticated, the
//      app calls `usePreferences()` somewhere in the tree — most usefully
//      on /settings/theme + /settings/notifications. If a call has already
//      populated the react-query cache for `['users','me','preferences']`,
//      ThemeProvider syncs from it lazily via the `useResolvedTheme`
//      hook.
//   3. Setting the theme through `setTheme()` writes the cookie hint and
//      fires a PATCH /api/users/me/preferences via the consumer's chosen
//      mutation hook (Theme.tsx wires it through `useUpdatePreferences`).
//
// API surface kept identical to Phase 6 so `ThemeToggle.tsx` and the
// AppShell continue to work without changes:
//
//   useTheme(): { theme: 'light' | 'dark', source, setTheme, toggleTheme }
//
// The `source` discriminator now distinguishes 'os' (no preference yet),
// 'manual' (user toggled in-session), and 'server' (resolved from the
// preferences endpoint).
//
// Q-9: cookie write (`dailydeck.theme=<resolved>`) lifetime 1 year, Path=/,
// SameSite=Lax; no Secure in dev (Vite is http) so the cookie round-trips.

import * as React from 'react';

export type ThemeName = 'light' | 'dark';
/** Three-option preference (server shape). 'system' resolves to 'light' or 'dark'. */
export type ThemePreference = 'light' | 'dark' | 'system';
export type ThemeSource = 'os' | 'manual' | 'server';

export interface ThemeContextValue {
  theme: ThemeName;
  source: ThemeSource;
  /** The user's stored preference ('system' is allowed). Settings page reads this. */
  preference: ThemePreference;
  setTheme: (next: ThemeName) => void;
  /** Apply a `system | light | dark` preference and resolve to the visible theme. */
  setPreference: (next: ThemePreference) => void;
  toggleTheme: () => void;
}

const ThemeContext = React.createContext<ThemeContextValue | null>(null);

const COOKIE_NAME = 'dailydeck.theme';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365; // 1 year

function getOsTheme(): ThemeName {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function readCookieTheme(): ThemeName | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(/(?:^|;\s*)dailydeck\.theme=(light|dark)(?:;|$)/);
  return m ? (m[1] as ThemeName) : null;
}

function writeCookieTheme(resolved: ThemeName): void {
  if (typeof document === 'undefined') return;
  // Note: deliberately no Secure flag — production hosting (HTTPS) sets it
  // automatically via the upstream proxy; dev (Vite over http) needs the
  // cookie to round-trip without it.
  document.cookie = `${COOKIE_NAME}=${resolved}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;
}

function applyDocumentTheme(theme: ThemeName): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
}

function resolvePreference(pref: ThemePreference): ThemeName {
  if (pref === 'system') return getOsTheme();
  return pref;
}

export interface ThemeProviderProps {
  children: React.ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps): JSX.Element {
  // Initial theme: cookie hint > OS preference. The cookie write that
  // populated this hint lives on a successful preference fetch (see
  // useThemeSyncFromPreferences below) or on a direct setTheme call.
  const [theme, setThemeState] = React.useState<ThemeName>(
    () => readCookieTheme() ?? getOsTheme(),
  );
  const [preference, setPreferenceState] = React.useState<ThemePreference>(() => {
    const cookie = readCookieTheme();
    return cookie ?? 'system';
  });
  const [source, setSource] = React.useState<ThemeSource>(() =>
    readCookieTheme() !== null ? 'server' : 'os',
  );

  // Apply data-theme on every change.
  React.useEffect(() => {
    applyDocumentTheme(theme);
  }, [theme]);

  // OS-theme listener — only honoured when the resolved preference is 'system'.
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent): void => {
      if (preference === 'system') {
        const next: ThemeName = e.matches ? 'dark' : 'light';
        setThemeState(next);
        writeCookieTheme(next);
      }
    };
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [preference]);

  const setTheme = React.useCallback((next: ThemeName) => {
    setThemeState(next);
    setPreferenceState(next);
    setSource('manual');
    writeCookieTheme(next);
  }, []);

  const setPreference = React.useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    const resolved = resolvePreference(next);
    setThemeState(resolved);
    setSource('server');
    writeCookieTheme(resolved);
  }, []);

  const toggleTheme = React.useCallback(() => {
    setThemeState((prev) => {
      const next: ThemeName = prev === 'light' ? 'dark' : 'light';
      writeCookieTheme(next);
      return next;
    });
    setPreferenceState((prev) => {
      // If the user was on 'system', a manual toggle pins to the new value.
      if (prev === 'system') {
        return getOsTheme() === 'dark' ? 'light' : 'dark';
      }
      return prev === 'light' ? 'dark' : 'light';
    });
    setSource('manual');
  }, []);

  const value = React.useMemo<ThemeContextValue>(
    () => ({ theme, source, preference, setTheme, setPreference, toggleTheme }),
    [theme, source, preference, setTheme, setPreference, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = React.useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme() must be used within a <ThemeProvider>');
  }
  return ctx;
}
